import { BLOCK_CAPS, WORLD_PACKAGE_LIMITS as L, } from './format.js';
import { propsProblems } from './props.js';
import { UNSAFE_TEXT, insidePolygon, isNumber, isRecord, problem, show } from './util.js';
function pathLength(points, closed) {
    let sum = 0;
    const last = closed ? points.length : points.length - 1;
    for (let i = 0; i < last; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        sum += Math.hypot(b[0] - a[0], b[1] - a[1]);
    }
    return sum;
}
/**
 * At most one licht block sets the world's sun or sky: a block that scales them (not 1) or whose
 * states change them. `touched` is the ids of the licht blocks a state changes sun or sky of.
 */
export function lightRules(blocks, touched, index) {
    const setting = blocks.filter((b) => b.type === 'licht' && (b.sun !== 1 || b.sky !== 1 || touched.has(b.id)));
    return setting
        .slice(1)
        .map((b) => problem(`/blocks/${index(b)}`, 'hooguit één licht-blok zet de zon of de lucht'));
}
export const V2_ROOT_KEYS = ['descriptionEn', 'staged', 'picture', 'props'];
/** Format 2's keys a package may leave out (ADR-053). */
export const V2_OPTIONAL_KEYS = ['houdVrij'];
/** Format 2's own top-level keys (V1); the picture is added to the files the package names. */
export function extrasProblems(json, uses) {
    const out = [];
    const text = json['descriptionEn'];
    if (text !== undefined &&
        (typeof text !== 'string' ||
            text.trim().length < 1 ||
            text.length > L.descriptionEn ||
            UNSAFE_TEXT.test(text)))
        out.push(problem('/descriptionEn', `moet een tekst van 1 tot ${L.descriptionEn} tekens zijn`));
    if (json['staged'] !== undefined && typeof json['staged'] !== 'boolean')
        out.push(problem('/staged', 'moet true of false zijn'));
    const picture = json['picture'];
    if (picture !== undefined && picture !== null) {
        if (typeof picture === 'string' && !picture.endsWith('.jpg'))
            out.push(problem('/picture', 'moet een .jpg-bestand zijn'));
        uses.push({ path: '/picture', name: picture, kind: 'texture' });
    }
    if (json['props'] !== undefined)
        out.push(...propsProblems(json['props'], '/props'));
    if (json['houdVrij'] !== undefined)
        out.push(...keepClearProblems(json['houdVrij']));
    return out;
}
/** `houdVrij` (ADR-053): at most four sectors {richting −180–180, breedte 5–120}, nothing else. */
function keepClearProblems(value) {
    if (!Array.isArray(value) || value.length > L.keepClear)
        return [problem('/houdVrij', `moet een lijst van hooguit ${L.keepClear} sectoren zijn`)];
    return value.flatMap((sector, i) => isRecord(sector) &&
        Object.keys(sector).length === 2 &&
        isNumber(sector['richting']) &&
        Math.abs(sector['richting']) <= 180 &&
        isNumber(sector['breedte']) &&
        sector['breedte'] >= 5 &&
        sector['breedte'] <= 120
        ? []
        : [problem(`/houdVrij/${i}`, 'moet {richting: −180–180, breedte: 5–120} zijn')]);
}
/**
 * Rules between blocks (format 2): one plaatsruimte; its begin a room; room ids unique; views on
 * floors; staged false only with rooms; at most BLOCK_CAPS.rotating draaien-blocks.
 */
export function blockRules(blocks, staged, index, featureCount = 0) {
    const out = [];
    for (const b of blocks) {
        const at = `/blocks/${index(b)}`;
        if (b.type === 'draaien' && Math.hypot(...b.axis) < 0.1)
            out.push(problem(`${at}/axis`, 'moet een richting hebben (langer dan 0,1)'));
        if (b.type === 'route' && pathLength(b.path, b.closed) < 1)
            out.push(problem(`${at}/path`, 'moet minstens 1 m lang zijn'));
        if (b.type === 'zwerm' && b.kind === 'vissen' && b.colours.length < 2)
            out.push(problem(`${at}/colours`, 'vissen hebben twee kleuren: [donkerst, lichtst]'));
    }
    const spaces = blocks.filter((b) => b.type === 'plaatsruimte');
    spaces
        .slice(1)
        .forEach((b) => out.push(problem(`/blocks/${index(b)}`, 'een wereld heeft hooguit één plaatsruimte')));
    const space = spaces[0];
    if (space) {
        const at = `/blocks/${index(space)}`;
        // Placement-only for now (ADR-050): a world for placing work has no features to look at.
        if (featureCount > 0)
            out.push(problem('/features', 'een wereld met een plaatsruimte heeft geen onderdelen (features)'));
        space.walls.forEach((wall, i) => {
            if (wall.high > space.ceiling)
                out.push(problem(`${at}/walls/${i}/high`, 'mag niet hoger zijn dan het plafond (ceiling)'));
        });
        const rooms = space.rooms.map((r) => r.id);
        if (!rooms.includes(space.begin))
            out.push(problem(`${at}/begin`, `"${show(space.begin)}" is geen kamer (wel: ${rooms.join(', ')})`));
        if (new Set(rooms).size !== rooms.length)
            out.push(problem(`${at}/rooms`, 'twee kamers hebben dezelfde id'));
        space.rooms.forEach((room, i) => {
            const [x, , z] = room.view.pos;
            if (!space.floors.some((f) => insidePolygon(f.area, x, z)))
                out.push(problem(`${at}/rooms/${i}/view/pos`, 'staat niet op een vloer'));
            else if (!insidePolygon(room.area, x, z))
                out.push(problem(`${at}/rooms/${i}/view/pos`, 'staat niet in de kamer zelf (area)'));
        });
        // Where the pieces start: on a floor, from a line inside the first area (placement.ts START_GAP is 0.4 m).
        space.start.areas.forEach((area, i) => {
            const middle = [(area.x[0] + area.x[1]) / 2, (area.z[0] + area.z[1]) / 2];
            if (!space.floors.some((f) => insidePolygon(f.area, middle[0], middle[1])))
                out.push(problem(`${at}/start/areas/${i}`, 'ligt niet op een vloer'));
        });
        const first = space.start.areas[0];
        const from = space.start.from;
        if (first && from !== null && (from < first.x[0] || from > first.x[1] - 0.4))
            out.push(problem(`${at}/start/from`, 'moet binnen de eerste rechthoek liggen, minstens 0,4 m voor zijn rand'));
    }
    if (staged === true && space)
        out.push(problem('/staged', 'moet false zijn met een plaatsruimte: op een podium tellen de vaste kasten (fittings) en het plafond (ceiling) niet'));
    if (staged === false && !space)
        out.push(problem('/staged', 'false kan alleen met een plaatsruimte (de kamers zijn dan de plekken)'));
    if (blocks.filter((b) => b.type === 'draaien').length > BLOCK_CAPS.rotating)
        out.push(problem('/blocks', `hooguit ${BLOCK_CAPS.rotating} draaien-blokken`));
    return out;
}
//# sourceMappingURL=v2.js.map